import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Plus, 
  FileText, 
  MoreVertical, 
  Trash2, 
  Eye, 
  Calendar,
  Building2,
  Loader2,
  FolderOpen
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GradeCard from "@/components/GradeCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportsList() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/reports/${deleteId}`);
      setReports(prev => prev.filter(r => r.id !== deleteId));
      toast.success("Report deleted successfully");
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    } finally {
      setDeleteId(null);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      A: "bg-emerald-100 text-emerald-700 border-emerald-200",
      B: "bg-lime-100 text-lime-700 border-lime-200",
      C: "bg-yellow-100 text-yellow-700 border-yellow-200",
      D: "bg-orange-100 text-orange-700 border-orange-200",
      F: "bg-red-100 text-red-700 border-red-200"
    };
    return colors[grade] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="pt-28 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Snapshot Reports</h1>
              <p className="text-slate-600">View and manage all generated business reports</p>
            </div>
            <Link to="/lookup">
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-report-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Report
              </Button>
            </Link>
          </div>
          
          {/* Reports Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : reports.length === 0 ? (
            <Card className="border-slate-200" data-testid="empty-state">
              <CardContent className="py-20 text-center">
                <FolderOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No reports yet</h3>
                <p className="text-slate-500 mb-6">Generate your first snapshot report to get started</p>
                <Link to="/lookup">
                  <Button className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="h-4 w-4 mr-2" />
                    Generate Report
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reports-grid">
              {reports.map((report) => (
                <Card 
                  key={report.id} 
                  className="border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                  data-testid={`report-card-${report.id}`}
                >
                  {/* Grade Header */}
                  <div className={`h-2 ${
                    report.overall_grade === 'A' ? 'bg-emerald-500' :
                    report.overall_grade === 'B' ? 'bg-lime-500' :
                    report.overall_grade === 'C' ? 'bg-yellow-500' :
                    report.overall_grade === 'D' ? 'bg-orange-500' : 'bg-red-500'
                  }`} />
                  
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 line-clamp-1">
                            {report.business_name}
                          </h3>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Calendar className="h-3 w-3" />
                            {new Date(report.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`report-menu-${report.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <Link to={`/report/${report.id}`}>
                            <DropdownMenuItem>
                              <Eye className="h-4 w-4 mr-2" />
                              View Report
                            </DropdownMenuItem>
                          </Link>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => setDeleteId(report.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Score Display */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-sm text-slate-500 mb-1">Overall Score</div>
                        <div className="text-3xl font-bold text-slate-900 font-['Outfit']">
                          {report.overall_score}%
                        </div>
                      </div>
                      <GradeCard grade={report.overall_grade} size="lg" />
                    </div>
                    
                    {/* Mini Grade Preview */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {['listings', 'reviews', 'social', 'website'].map((section) => (
                        <div key={section} className="text-center">
                          <div className="text-xs text-slate-500 mb-1 capitalize">{section}</div>
                          <Badge className={`${getGradeColor(report[section].grade)} text-xs font-semibold`}>
                            {report[section].grade}
                          </Badge>
                        </div>
                      ))}
                    </div>
                    
                    {/* View Button */}
                    <Link to={`/report/${report.id}`}>
                      <Button 
                        variant="outline" 
                        className="w-full border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                        data-testid={`view-report-btn-${report.id}`}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        View Full Report
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this report? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
